import { useCallback, useEffect, useRef, useState } from 'react';

import { minutes, seconds } from '@utils/milliseconds';
import { Timeout } from '@utils/types';

// Cap the recording time to 2 minutes
const MAX_RECORDING_TIME_MS = minutes(2);
// RMS amplitude threshold for silence detection
const SILENCE_RMS_THRESHOLD = 0.02;
// Threshold to detect actual speech vs ambient noise
const SPEECH_DETECTED_THRESHOLD = 0.01;
// If no noise is detected within 2 seconds -> stop the recording
const SILENCE_DURATION_MS = seconds(2);
// Byte at 128 represents silence.
const SILENCE_AT_BYTE_THRESHOLD = 128;

export type RecordingState = 'idle' | 'starting' | 'recording' | 'stopping';

export const useAudioRecorder = () => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<{
    mediaRecorder: MediaRecorder | null;
    audioContext: AudioContext | null;
    analyser: AnalyserNode | null;
    audioChunks: Blob[];
    stream: MediaStream | null;
    animationFrame: number | null;
    maxDurationTimer: Timeout | null;
    lastSoundTime: number;
    isRecording: boolean;
    speechDetected: boolean;
    analysisStarted: boolean;
  }>({
    mediaRecorder: null,
    audioContext: null,
    analyser: null,
    audioChunks: [],
    stream: null,
    animationFrame: null,
    maxDurationTimer: null,
    lastSoundTime: Date.now(),
    isRecording: false,
    speechDetected: false,
    analysisStarted: false,
  });

  const stopRecording = useCallback(() => {
    if (recorderRef.current.mediaRecorder && recorderRef.current.isRecording) {
      recorderRef.current.isRecording = false;
      setRecordingState('stopping');
      recorderRef.current.mediaRecorder.stop();
    }
  }, []);

  const cleanup = useCallback(() => {
    recorderRef.current.isRecording = false;

    if (recorderRef.current.animationFrame) {
      cancelAnimationFrame(recorderRef.current.animationFrame);
      recorderRef.current.animationFrame = null;
    }

    if (recorderRef.current.maxDurationTimer) {
      clearTimeout(recorderRef.current.maxDurationTimer);
      recorderRef.current.maxDurationTimer = null;
    }

    if (recorderRef.current.audioContext) {
      recorderRef.current.audioContext.close();
      recorderRef.current.audioContext = null;
    }

    if (recorderRef.current.stream) {
      recorderRef.current.stream.getTracks().forEach((track) => track.stop());
      recorderRef.current.stream = null;
    }

    recorderRef.current.mediaRecorder = null;
    recorderRef.current.analyser = null;
    recorderRef.current.audioChunks = [];
    setAudioLevel(0);
  }, []);

  const analyzeAudio = useCallback(() => {
    if (!recorderRef.current.analyser) return;

    // The analyzer returns the raw waveform, which are bytes in the range 0-255.
    const dataArray = new Uint8Array(recorderRef.current.analyser.fftSize);
    recorderRef.current.analyser.getByteTimeDomainData(dataArray);

    // Compute the Root Mean Square (RMS) of the audio signal. This is a single value that represents the overall loudness of the audio.
    // We use this to determine if the audio is silent or not and to show audio level in the visualizer.
    const sumSquares = Array.from(dataArray).reduce((sum, byte) => {
      const normalized = (byte - SILENCE_AT_BYTE_THRESHOLD) / (255 - SILENCE_AT_BYTE_THRESHOLD); // The bytes need to be normalized to the range [-1, 1]
      return sum + normalized * normalized;
    }, 0);

    const rms = Math.sqrt(sumSquares / dataArray.length);

    // Scale the audio up for better visualization.
    setAudioLevel(Math.min(rms * 2, 1));

    if (rms > SPEECH_DETECTED_THRESHOLD) {
      recorderRef.current.speechDetected = true;
    }

    if (rms > SILENCE_RMS_THRESHOLD) {
      recorderRef.current.lastSoundTime = Date.now();
    } else {
      const silenceDuration = Date.now() - recorderRef.current.lastSoundTime;
      if (silenceDuration >= SILENCE_DURATION_MS) {
        stopRecording();
        return;
      }
    }

    recorderRef.current.animationFrame = requestAnimationFrame(analyzeAudio);
  }, [stopRecording]);

  const startRecording = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      recorderRef.current.audioChunks = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderRef.current.stream = stream;

      const audioContext = new AudioContext();
      recorderRef.current.audioContext = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      recorderRef.current.analyser = analyser;
      source.connect(analyser);

      const mediaRecorder = new MediaRecorder(stream);
      recorderRef.current.mediaRecorder = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recorderRef.current.audioChunks.push(event.data);
          // Start analysis on first chunk to avoid initial word cutoff
          if (!recorderRef.current.analysisStarted) {
            recorderRef.current.analysisStarted = true;
            setRecordingState('recording');
            analyzeAudio();
            recorderRef.current.maxDurationTimer = setTimeout(() => {
              stopRecording();
            }, MAX_RECORDING_TIME_MS);
          }
        }
      };

      mediaRecorder.onstart = () => {
        recorderRef.current.isRecording = true;
        recorderRef.current.speechDetected = false;
        recorderRef.current.lastSoundTime = Date.now();
        recorderRef.current.analysisStarted = false;
      };

      mediaRecorder.onstop = () => {
        // No need to send if no audio was detected.
        if (!recorderRef.current.speechDetected) {
          cleanup();
          setRecordingState('idle');
          return;
        }

        const audioBlob = new Blob(recorderRef.current.audioChunks, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, {
          type: 'audio/webm',
        });

        cleanup();
        setRecordingState('idle');

        if (onCompleteRef.current) {
          onCompleteRef.current(audioFile);
        }
      };

      setRecordingState('starting');
      mediaRecorder.start(100);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to access microphone. Please check permissions.',
      );
      cleanup();
      setRecordingState('idle');
    }
  }, [analyzeAudio, cleanup, stopRecording]);

  const onCompleteRef = useRef<((file: File) => void) | null>(null);

  const setOnComplete = useCallback((callback: (file: File) => void) => {
    onCompleteRef.current = callback;
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    recordingState,
    audioLevel,
    error,
    startRecording,
    stopRecording,
    setOnComplete,
  };
};
